import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { MAX_NAME_LENGTH } from '~/consts';
import { BottomSheet } from '~/modules/elements/akui/bottom-sheet';
import { Icon } from '~/modules/elements/akui/icon';
import { serverRpc } from '~/modules/remote-mic/network/client';
import { ControlDescriptor } from '~/routes/keyboard-help/controls';
import { remoteButtonIcons } from '~/routes/keyboard-help/remote-button-icons';
import { InfoButton, InfoLine } from '~/routes/remote-mic/components/info-reveal';
import NumericInput from '~/routes/remote-mic/components/numeric-input';

interface Props {
  control: ControlDescriptor;
  onActivate: (name: string) => void;
  // Rows of a settings list share one card, so they carry no rounding or border of their own.
  inCard?: boolean;
  // The action the screen exists to press, named by the screen itself (see `HelpEntry.primary`).
  // A screen of equal choices names none, and then nothing on it is filled.
  primary?: boolean;
  // Ask before firing this one. Set by the screen renderer, not by the descriptor: the same ✕ is
  // ordinary on the television and on any phone but the one it removes.
  confirm?: { title: string; description: string; action: string };
}

const ROW = 'flex h-15 w-full items-center gap-3 border-none px-4.5 text-left text-[1.0625rem] text-white';
const CARD_ROW = `${ROW} bg-transparent transition-colors duration-150 active:bg-white/6`;
const LOOSE = 'rounded-[1.125rem] bg-black/50 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]';
const PRESS = 'cursor-pointer transition-transform duration-100 active:scale-[0.98]';

// Every mirrored control renders at the phone's own control scale rather than at the TV's, which is
// what the redesign was asked for: one height for a row, one for a primary action, and no control
// drawn on top of another.
export default function RemoteControl({ control, onActivate, inCard = false, primary = false, confirm }: Props) {
  const activate = () => onActivate(control.name);

  switch (control.type) {
    case 'button':
      return (
        <ButtonControl control={control} onActivate={onActivate} inCard={inCard} primary={primary} confirm={confirm} />
      );
    case 'switch':
      return (
        <button
          type="button"
          onClick={activate}
          disabled={control.disabled}
          data-test={`control-${control.name}`}
          data-control-type="switch"
          className={twMerge(inCard ? CARD_ROW : `${ROW} ${LOOSE} ${PRESS}`, control.disabled && 'opacity-40')}>
          <span className="min-w-0 flex-1 truncate">{control.label}</span>
          <span className="text-active shrink-0 text-[1.0625rem] font-bold">{control.value}</span>
        </button>
      );
    case 'checkbox':
      return (
        <button
          type="button"
          onClick={activate}
          disabled={control.disabled}
          data-test={`control-${control.name}`}
          data-control-type="checkbox"
          data-checked={control.checked}
          className={twMerge(inCard ? CARD_ROW : `${ROW} ${LOOSE} ${PRESS}`, control.disabled && 'opacity-40')}>
          <CheckBox checked={control.checked} />
          <span className="min-w-0 flex-1 truncate">{control.label}</span>
        </button>
      );
    case 'text':
      return <TextControl control={control} inCard={inCard} />;
    case 'select':
      return <SelectControl control={control} inCard={inCard} />;
    case 'input-lag':
      return <InputLagControl control={control} inCard={inCard} />;
    default:
      control satisfies never;
      return null;
  }
}

export function CheckBox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={twMerge(
        'flex size-6.5 shrink-0 items-center justify-center rounded-md text-black',
        checked ? 'bg-active shadow-[inset_0_0_0_2px_rgba(0,0,0,0.5)]' : 'bg-white/10',
      )}>
      {checked && <Icon icon="ic:baseline-check" size={4.5} />}
    </span>
  );
}

// A free-form field. The row shows the value and opens a sheet holding the input and an explicit
// Apply, so a half-typed name never reaches the TV and the phone's own keyboard has somewhere to go.
function TextControl({ control, inCard }: { control: Extract<ControlDescriptor, { type: 'text' }>; inCard: boolean }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(control.value);

  const openEditor = () => {
    setDraft(control.value);
    setOpen(true);
  };

  const apply = () => {
    void serverRpc.input.setControlValue(control.name, draft);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={openEditor}
        disabled={control.disabled}
        data-test={`control-${control.name}`}
        data-control-type="text"
        className={twMerge(inCard ? CARD_ROW : `${ROW} ${LOOSE} ${PRESS}`, control.disabled && 'opacity-40')}>
        <span className="min-w-0 flex-1 truncate">{control.value || control.placeholder || control.label}</span>
        <Icon icon="ic:baseline-edit" size={5} className="shrink-0 opacity-70" />
      </button>
      <BottomSheet open={open} onClose={() => setOpen(false)} title={control.label}>
        <div className="flex flex-col gap-3">
          <div className="flex h-16 items-center rounded-[1.125rem] bg-white/8 px-4.5 shadow-[inset_0_0_0_2px_#facc15]">
            <input
              type="text"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                // Enter is "done" on a phone keyboard, so it applies rather than making the person
                // dismiss the keyboard to reach the button.
                if (event.key === 'Enter') apply();
              }}
              maxLength={MAX_NAME_LENGTH}
              placeholder={control.placeholder}
              autoFocus
              data-test="control-value-input"
              className="ph-no-capture h-full min-w-0 flex-1 border-none bg-transparent text-[1.375rem] font-bold outline-none"
            />
          </div>
          <button
            type="button"
            onClick={apply}
            data-test="control-value-apply"
            className="bg-active flex h-16 cursor-pointer items-center justify-center rounded-[1.125rem] border-none text-[1.1875rem] font-bold text-black transition-transform duration-100 active:scale-[0.98]">
            Apply
          </button>
        </div>
      </BottomSheet>
    </>
  );
}

// A list with exactly one chosen. Tapping an option applies it and closes: unlike a half-typed name,
// a chosen option is already complete, so there is nothing to confirm.
function SelectControl({
  control,
  inCard,
}: {
  control: Extract<ControlDescriptor, { type: 'select' }>;
  inCard: boolean;
}) {
  const [open, setOpen] = useState(false);
  const chosen = control.options.find((option) => option.value === control.value);

  const choose = (option: (typeof control.options)[number]) => {
    // Nothing is sent for a locked option: the host refuses it as well (see `mic-slot.tsx`), but a
    // press that quietly closes the sheet and changes nothing is worse than one that does not move.
    if (option.disabled) return;

    void serverRpc.input.setControlValue(control.name, option.value);
    setOpen(false);
  };

  const empty = control.options.length === 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={control.disabled || empty}
        data-test={`control-${control.name}`}
        data-control-type="select"
        className={twMerge(
          inCard ? CARD_ROW : `${ROW} ${LOOSE} ${PRESS}`,
          (control.disabled || empty) && 'opacity-40',
        )}>
        <span className="min-w-0 flex-1 truncate">{control.label}</span>
        <span className="text-active min-w-0 shrink truncate text-[1.0625rem] font-bold">
          {chosen?.label ?? (empty ? 'Nothing to choose from' : '')}
        </span>
        <Icon icon="ic:baseline-expand-more" size={5.5} className="shrink-0 opacity-70" />
      </button>
      <BottomSheet open={open} onClose={() => setOpen(false)} title={control.label}>
        <div className="flex flex-col gap-2">
          {control.options.map((option) => {
            const selected = option.value === control.value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => choose(option)}
                disabled={option.disabled}
                data-test={`control-option-${option.value}`}
                data-selected={selected}
                data-disabled={option.disabled === true}
                className={twMerge(
                  'flex h-15 items-center gap-3 rounded-2xl border-none px-4.5 text-left text-[1.0625rem] transition-colors duration-150',
                  selected
                    ? 'bg-active font-bold text-black'
                    : 'bg-white/6 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]',
                  // Shown rather than dropped: the point of a locked row is that it says where the
                  // microphone went, so the one way to get it back is legible from here.
                  option.disabled && 'opacity-45',
                )}>
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.note && <span className="shrink-0 text-[0.9375rem] text-white/55">{option.note}</span>}
              </button>
            );
          })}
        </div>
      </BottomSheet>
    </>
  );
}

function InputLagControl({
  control,
  inCard,
}: {
  control: Extract<ControlDescriptor, { type: 'input-lag' }>;
  inCard: boolean;
}) {
  const [value, setValue] = useState(control.value);
  useEffect(() => setValue(control.value), [control.value]);

  const change = (newValue: number) => {
    setValue(newValue);
    void serverRpc.settings.setInputLag(newValue);
  };

  return (
    <div
      // Rule A puts the stepper in the card with the other value controls, and a card supplies the
      // background its rows sit on - so this one brings only the inset the rows get from `ROW`.
      className={twMerge('flex flex-col gap-2.5', inCard && 'px-4.5 py-3.5')}
      data-test={`control-${control.name}`}
      data-control-type="input-lag">
      {/* The stepper is a number between two arrows and says nothing about itself, so the label it
          would otherwise have gone without lives above it, with the explanation behind an `i`. */}
      <span className="flex items-center gap-2 text-[0.8125rem] tracking-[0.08em] text-white/60 uppercase">
        {control.label}
        <InfoButton infoKey="InputLag" />
      </span>
      <NumericInput value={value} onChange={change} disabled={control.disabled} unit="ms" data-test="game-input-lag" />
      <InfoLine infoKey="InputLag" surface={inCard ? 'gradient' : 'ground'}>
        Increase if the sound lags behind the lyrics.
      </InfoLine>
    </div>
  );
}

// The only mirrored control that can ask before it acts. Everything else on the phone is a direct
// echo of a television control, which is the pattern this deliberately breaks once: clearing your
// own slot is the one press whose consequence lands on the person holding the phone.
function ButtonControl({
  control,
  onActivate,
  inCard,
  primary,
  confirm,
}: {
  control: Extract<ControlDescriptor, { type: 'button' }>;
  onActivate: (name: string) => void;
  inCard: boolean;
  primary: boolean;
  confirm?: { title: string; description: string; action: string };
}) {
  const [open, setOpen] = useState(false);
  const activate = () => onActivate(control.name);

  // A screen that names this control as its primary is saying it is the way ON, whatever the
  // TV needs the variant for. Setup microphones is the case: `Done` is a `variant="back"` so
  // Backspace leaves the screen, but every exit runs `commitMicSetup()`, so a leading ← on the
  // phone promises a way back to something that is in fact apply-and-close.
  const isBack = control.variant === 'back' && !primary;
  const rightIcon = isBack || control.icon === null ? undefined : remoteButtonIcons[control.icon ?? 'forward'];

  return (
    <>
      <button
        type="button"
        onClick={confirm ? () => setOpen(true) : activate}
        disabled={control.disabled}
        data-test={`control-${control.name}`}
        data-control-type="button"
        className={twMerge(
          inCard
            ? CARD_ROW
            : `flex h-16 items-center gap-3 border-none px-4.5 text-left text-[1.0625rem] text-white ${LOOSE} ${PRESS}`,
          primary && 'bg-active text-[1.1875rem] font-bold text-black shadow-none',
          isBack && 'bg-black/30',
          control.disabled && 'pointer-events-none opacity-40',
        )}>
        {isBack && <Icon icon="ic:baseline-arrow-back" size={5.5} className="shrink-0" />}
        <span className="min-w-0 flex-1 truncate">{control.label}</span>
        {/* Quieter than the label and smaller than it: what the row IS, beside what it is called. */}
        {control.note && (
          <span className={twMerge('shrink-0 text-[0.9375rem]', primary ? 'text-black/55' : 'text-white/45')}>
            {control.note}
          </span>
        )}
        {rightIcon && <Icon icon={rightIcon} size={5.5} className={twMerge('shrink-0', !primary && 'opacity-70')} />}
      </button>
      <BottomSheet open={open} onClose={() => setOpen(false)} title={confirm?.title ?? ''}>
        <div className="flex flex-col gap-3">
          <p className="text-[1.0625rem] leading-[1.45] text-white/70">{confirm?.description}</p>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              activate();
            }}
            data-test="control-confirm"
            className="flex h-16 cursor-pointer items-center justify-center rounded-[1.125rem] border-none bg-black/50 text-[1.1875rem] font-bold text-red-400 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] transition-transform duration-100 active:scale-[0.98]">
            {confirm?.action}
          </button>
          {/* Staying is the safe answer, so it is the one drawn as the thing to press. */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-test="control-confirm-cancel"
            className="bg-active flex h-16 cursor-pointer items-center justify-center rounded-[1.125rem] border-none text-[1.1875rem] font-bold text-black transition-transform duration-100 active:scale-[0.98]">
            Stay
          </button>
        </div>
      </BottomSheet>
    </>
  );
}
